import { Moment } from "moment";

import { Nullable } from "@ctra/utils";

import { DataDescriptorEntity } from "../data-dictionary";
import { FarmEntity } from "../farms";
import { UserEntity } from "../user";

export interface GamePlanGoal {
  unit: string;
  value: number;
}

export interface GamePlanTimePeriod {
  startEpoch: number;
  endEpoch: number;
}

export enum TodoItemStatus {
  ToDo = 0,
  Completed = 1
}

export enum TodoUpdateAction {
  advance = "advance",
  update = "update"
}

export interface TodoItemEntity {
  id: string;
  createdBy: UserEntity["id"];
  modifiedEpoch: number;
  label: string;
  status: TodoItemStatus;
  startEpoch: number;
  endEpoch: number;
  dueDateTs: Nullable<string>;
  assignedTo: UserEntity["id"];
  /**
   * reserved for metadata added by selectors
   */
  meta?: {
    farmID: FarmEntity["id"];
    gamePlanID: GamePlanEntity["id"];
  };
}

export interface GamePlanEntity {
  id: string;
  dataDescriptorID: DataDescriptorEntity["id"];
  farmID: FarmEntity["id"];
  title: string;
  description: string;
  timePeriod: GamePlanTimePeriod;
  goal: GamePlanGoal;
  taskList: Array<TodoItemEntity["id"]>;
  unitSystem: string;
}

export type GamePlanList = Record<GamePlanEntity["id"], GamePlanEntity>;
export type TodoItemList = Record<TodoItemEntity["id"], TodoItemEntity>;

export type GamePlanState = {
  entities: GamePlanList;
  todos: TodoItemList;
};

export interface TaskListFormValues {
  id?: TodoItemEntity["id"];
  label: TodoItemEntity["label"];
  status: TodoItemEntity["status"];
  dueDateMoment?: Moment;
  assignedTo?: Nullable<TodoItemEntity["assignedTo"]>;
  createdBy?: TodoItemEntity["createdBy"];
}

export interface GamePlanFormValues {
  id?: GamePlanEntity["id"];
  title: string;
  description?: string;
  timePeriod: [Nullable<Moment>, Nullable<Moment>];
  farmID: Nullable<FarmEntity["id"]>;
  dataDescriptorID: Nullable<DataDescriptorEntity["id"]>;
  hasTarget: number;
  goal?: number;
  taskList: Array<TaskListFormValues>;
  unitSystem: string;
}

export interface TaskListRequestPayload {
  id?: TodoItemEntity["id"];
  label: TodoItemEntity["label"];
  status: TodoItemEntity["status"];
  assignedTo?: TodoItemEntity["assignedTo"];
  createdBy?: TodoItemEntity["createdBy"];
  dueDateTs?: TodoItemEntity["dueDateTs"];
}

export interface GamePlanRequestPayload {
  id?: GamePlanEntity["id"];
  startEpoch: number;
  endEpoch: number;
  farmID: Nullable<FarmEntity["id"]>;
  goal: {
    unit?: string;
    // for some reason this needs to be a string
    value: Nullable<string>;
    unitSystem?: string;
  };
  dataDescriptorID: Nullable<DataDescriptorEntity["id"]>;
  title: string;
  description: string;
  taskList: Array<TaskListRequestPayload>;
}
